/**
 * ЕДИНЫЙ источник гейтов входа на экраны настроек. Ключ — маршрут.
 *
 * До 2026-07-26 гейт жил в ДВУХ местах — в реестре пунктов меню
 * (`shared/config/settingsMenuItems.ts`) и в реестре карточек хаба
 * (`features/settings/config/settingsHubCards.tsx`), — и они разошлись:
 * у карточек «Cron/Планировщик», «Биллинг» и «Задачи» гейта не было вовсе,
 * при загейченных пунктах меню. Персона `profile_role_10` (9 прав, из
 * settings-гейтов только `tasks.view` и `customers.view`) видела в хабе
 * 25 карточек, две из которых меню ей не показывало: карточка открывалась,
 * экран рисовался, и оператор упирался в 403 на первом же запросе.
 * Два согласованных списка хуже одного: расхождение невидимо до приёмки.
 *
 * ГЕЙТ ВХОДА = ПРАВО ЧТЕНИЯ API ЭКРАНА. Не право записи: карточка и пункт
 * меню — это вход на экран, а запись внутри экрана гейтится отдельно (иначе
 * предикат меню и предикат экрана расходятся — ровно тот дефект, что чинили
 * в poller и vendor-profiles).
 *
 * `null` означает «права нет намеренно», и у каждой такой записи ниже стоит
 * причина. Список ЗАКРЫТ: он обязан покрывать каждый достижимый маршрут
 * настроек — и каждый пункт меню, и каждую не-«Скоро» карточку хаба. Опечатка
 * в пути даёт промах по карте, а промах молча снял бы гейт, поэтому полноту
 * карты проверяют тесты (`settingsMenuGate.test.ts`,
 * `settingsHubCardGates.test.ts`).
 *
 * Новых прав здесь не изобретать: литерал должен быть достижим либо через
 * legacy-мост, либо через V5-native профильный грант. `system.settings` не
 * приходит из legacy-моста, но теперь достижим вторым способом; это не повод
 * гейтить им всю группу — только экран с совпадающим backend middleware.
 */
export type SettingsGate = {
  /** Право ЧТЕНИЯ API экрана. */
  permission?: string;
  /** Хотя бы одно из операционных прав открывает экран. */
  anyPermissions?: string[];
  /** Роль, если у API экрана прав не существует вовсе (см. `/settings/plugins`). */
  role?: string;
};

export const SETTINGS_ROUTE_GATES: Readonly<Record<string, SettingsGate | null>> = {
  // Хаб настроек. Права нет: экран сам фильтрует карточки по правам и без прав
  // показывает только «Скоро», тему и основную форму.
  '/settings': null,

  // ——— Права нет намеренно: защищать нечего ———

  // GET/PUT /api/v1/user/settings — только аутентификация, без права
  // (settings/delivery/http/settings_handler.go:161-167; PUT числится осознанным
  // исключением в routeguard: explicitMutationExemption). Это персональные
  // настройки самого оператора, права им не нужно.
  '/settings/theme': null,
  // Слайс observer-admin.6 (2026-08-27): экран перестал быть заглушкой — теперь это
  // навигационный лендинг с реальными ссылками на пять построенных подэкранов
  // (`.1`-`.5`, все под settings.observer.manage). Гейт вернулся, потому что содержимое
  // стало настоящим (тот же гейт, что у всех детей) — фантомным он был только пока сама
  // страница ничего не показывала.
  '/settings/observer': { permission: 'settings.observer.manage' },
  // «Скоро»: ни маршрута в роутере, ни backend. Гейт ставится, когда появится
  // контур — по праву его хендлера.
  '/settings/notifications': null,
  // Swagger UI. Права нет: /swagger/public/* и /swagger/admin/* смонтированы БЕЗ
  // auth-middleware (cmd/customer-service/main.go:768,773) — поверхность открыта
  // любому, и гейт в меню защитой не был бы. Открытость эндпоинта — отдельный долг.
  'open-swagger': null,

  // ——— Гейт по роли: прав у API не существует ———
  // /api/v1/plugins загейчен пер-маршрутно (plugins.view на чтения, plugins.manage
  // на install/run/status/delete — plugin/delivery/http/handler.go:43-49), но ни
  // одного из этих литералов НЕТ в public.auth_item (проверено read-only на wn-227
  // 2026-07-26). Фактически проходит только роль administrator.
  '/settings/plugins': { role: 'administrator' },
  // Основные настройки пока сохраняют прежний более строгий role-only UI-контракт.
  // Не расширяем их достижимость пассажиром в срезе equipment; отдельная сверка
  // basic с появившимися V5-native грантами нужна самостоятельной задачей.
  '/settings/basic': { role: 'administrator' },
  // Первый срез ITEM_7 читает и пишет erp.settings под system.settings. Это право
  // можно выдать V5-профилю прямым грантом, поэтому вход обязан совпадать с
  // RequirePermission backend; equipment.view здесь недостаточно.
  '/settings/equipment': { permission: 'system.settings' },
  // Витрина профилей вендоров SNMP. Роут защищён РОЛЬЮ, а не правом
  // (router.tsx: ProtectedRoute requiredRole="administrator"). Роль сравнивается
  // буквально, канон — `administrator` (backend auth/domain/role.go:52).
  '/settings/snmp-vendor-profiles': { role: 'administrator' },

  // ——— Гейт по праву ЧТЕНИЯ API ———
  // /api/v1/license/* целиком под license.view (license/delivery/http/handler.go:37).
  '/settings/license': { permission: 'license.view' },
  // GET /api/v1/settings/modules под modules.view (settings/modules/.../handler.go:60).
  '/settings/modules': { permission: 'modules.view' },
  // Экран расписаний читает GET /api/v1/modules/schedules под modules.view
  // (module/delivery/http/handler.go:67). Тот же экран (ModuleSchedulesPage) висит
  // и на /settings/modules/schedules — маршрут без пункта меню и без карточки,
  // достижим только прямым URL; гейт входа туда не ставится, потому что входа нет.
  '/settings/cron': { permission: 'modules.view' },
  // Группа биллинг-модуля целиком под modules.manage
  // (settings/billing/.../billing_module_handler.go:101) — чтения без права нет.
  '/settings/modules/billing': { permission: 'modules.manage' },
  // /api/v1/settings/map/* целиком под settings.map.manage
  // (settings/map/delivery/http/handler.go:61).
  '/settings/map': { permission: 'settings.map.manage' },
  // Типы и иконки узлов живут в API настроек карты (mapSettingsApi / nodeIconsApi).
  '/settings/node-types': { permission: 'settings.map.manage' },
  '/settings/node-icons': { permission: 'settings.map.manage' },
  // GET/PUT /api/v1/settings/map/geocoding — отдельная группа и отдельное право
  // (P2.3, 2026-08-09): креды геосервисов больше не гейтятся тем же правом,
  // что каталог карты (settings/map/delivery/http/handler.go, geocodingGroup).
  '/settings/geocoding': { permission: 'settings.geocoding.manage' },
  // /api/v1/settings/ldap/* под settings.ldap.manage (settings/ldap/.../handler.go:30).
  '/settings/ldap': { permission: 'settings.ldap.manage' },
  // /api/v1/settings/services/* под settings.services.manage
  // (settings/services/delivery/http/handler.go:42).
  '/settings/additional-services': { permission: 'settings.services.manage' },
  // GET /api/v1/additional-fields/* под customers.extra.read, запись — под
  // customers.extra.write (app/modules/customer/module.go, additionalFieldsGroup).
  // Вход — по праву ЧТЕНИЯ: список полей оператору видно и без права правки.
  '/settings/additional-fields': { permission: 'customers.extra.read' },
  // /api/v1/settings/documents/* под settings.documents.manage
  // (settings/documents/delivery/http/handler.go, group-level RequirePermission).
  // Право ОДНО на весь экран: смысл его в том, кто пишет тело шаблона, а тело уезжает в
  // печатную форму без чистки. Право V5-native — мост легаси-прав его намеренно не выдаёт
  // (RIGHT_SETTING = 6 стоит в escalation drop-set), поэтому по умолчанию его нет ни у кого,
  // и карточка с пунктом меню скрыты до явной выдачи администратором.
  '/settings/documents': { permission: 'settings.documents.manage' },
  // /api/v1/settings/observer/oids/* под settings.observer.manage
  // (settings/observer/delivery/http/handler.go, group-level RequirePermission) — каталог
  // OID для планировщика опроса wnm_observer, слайс observer-admin.1. Право ОДНО на весь
  // экран, V5-native той же природой, что settings.documents.manage выше (RIGHT_SETTING = 6
  // стоит в escalation drop-set) — по умолчанию его нет ни у кого.
  //
  // ОТДЕЛЬНЫЙ маршрут от `/settings/observer` (см. выше, «Права нет намеренно») — та
  // страница остаётся заглушкой-лендингом без backend (будущий observer-admin.6), эта —
  // реальный CRUD-экран каталога OID.
  '/settings/observer/oids': { permission: 'settings.observer.manage' },
  // /api/v1/settings/observer/profiles/* — тот же гейт, слайс observer-admin.2 (профили +
  // OID-назначение). Эталон не различает подэкраны одним правом на весь SettingsObserverHtml.
  '/settings/observer/profiles': { permission: 'settings.observer.manage' },
  // GET /api/v1/settings/observer/devices — сводная read-only таблица устройств под опросом,
  // слайс observer-admin.3, эталон `SettingsObserverHtml::deviceList`. Тот же гейт на весь
  // экран wnm_observer, экран ничего не пишет.
  '/settings/observer/devices': { permission: 'settings.observer.manage' },
  // GET /api/v1/settings/observer/readings — живые показания устройств под опросом, слайс
  // observer-admin.4, эталон `SettingsObserverHtml::argList`. Тот же гейт на весь экран
  // wnm_observer, экран ничего не пишет.
  '/settings/observer/readings': { permission: 'settings.observer.manage' },
  // GET /api/v1/settings/observer/log — журнал изменений показаний, слайс observer-admin.5,
  // эталон `SettingsObserverHtml::log`. Тот же гейт на весь экран wnm_observer, экран ничего не
  // пишет.
  '/settings/observer/log': { permission: 'settings.observer.manage' },
  // GET /api/v1/inventory/warehouses(/access-candidates|/:id) под inventory.view ИЛИ
  // inventory.catalog (inventory/delivery/http/handler.go, warehouseSettings-группа);
  // запись — под отдельным inventory.catalog.manage. Вход — по праву ЧТЕНИЯ: список
  // складов оператору видно и без права правки, как и в /settings/additional-fields.
  '/settings/warehouse': { permission: 'inventory.catalog' },
  // GET /api/v1/cable-lines/* под group-level cable_lines.view
  // (cable/delivery/http/handler.go:92).
  '/settings/cable-lines': { permission: 'cable_lines.view' },
  // Экран читает группы и статусы абонентов: GET /api/v1/customer-groups и
  // /api/v1/customer-statuses под customers.view; запись — customers.edit
  // (customer/.../group_handler.go:46, customer_status_handler.go:36).
  '/settings/customers': { permission: 'customers.view' },
  // Обычное чтение адресов не менялось, но этот settings-экран
  // предназначен для мутаций: его открывает любая из create/edit/delete.
  '/settings/address': {
    anyPermissions: ['address.create', 'address.edit', 'address.delete'],
  },
  // Вход на экран = ЧТЕНИЕ: GET /api/v1/devices/poller/settings под devices.view
  // (device/.../poller_settings_handler.go:122). Запись (PUT) требует
  // equipment.poller_control и гейтится внутри экрана.
  '/settings/poller': { permission: 'devices.view' },
  // Чтение справочников задач под tasks.view (task/delivery/http/handler.go:31-38);
  // запись — под system.settings и уже загейчена внутри экрана
  // (SettingsTasksPage canManageSettings).
  '/settings/tasks': { permission: 'tasks.view' },
};

/**
 * Гейт входа на экран настроек. Возвращает `null`, если права намеренно нет.
 *
 * Промах по карте — это НЕ «прав не нужно», а незаявленный маршрут: молча
 * вернуть `null` значило бы снять гейт опечаткой в пути. Поэтому промах
 * возвращает `undefined`, а полноту карты проверяют тесты.
 */
export function settingsGateFor(route: string): SettingsGate | null | undefined {
  return Object.prototype.hasOwnProperty.call(SETTINGS_ROUTE_GATES, route)
    ? SETTINGS_ROUTE_GATES[route]
    : undefined;
}

/** Гейт в виде полей `permission`/`role` — форма, в которой его ждут оба реестра. */
export function settingsGateFieldsFor(route: string): SettingsGate {
  const gate = settingsGateFor(route);
  return gate ?? {};
}
